import { z } from "zod";

import { API_PATHS } from "@/api/rooms";

const RoomMessageSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("cursor_update"),
    userId: z.string(),
    clientId: z.string(),
    cursor: z.object({
      longitude: z.number(),
      latitude: z.number(),
      viewBounds: z.object({
        north: z.number(),
        south: z.number(),
        east: z.number(),
        west: z.number(),
      }),
    }),
  }),
  z.object({
    type: z.literal("cursor_leave"),
    userId: z.string(),
    clientId: z.string(),
  }),
]);

type RoomMessage = z.infer<typeof RoomMessageSchema>;

interface LiveTransport<T> {
  send(message: T): void;
  open(roomId: string): void;
  close(): void;
  onMessage(message: T): void;
  onClose(): void;
  onOpen(): void;
  onError(error: Error): void;
  setMessageHandler(handler: (message: T) => void): void;
}

class WebsocketRoomTransport implements LiveTransport<RoomMessage> {
  private socket?: WebSocket;
  private messageHandler?: (message: RoomMessage) => void;
  private closeHandler?: () => void;
  private openHandler?: () => void;
  private errorHandler?: (error: Error) => void;
  private host: string;
  private tls: boolean;

  constructor(host: string, tls: boolean) {
    this.host = host;
    this.tls = tls;
  }

  private setupEventListeners() {
    if (!this.socket) {
      throw new Error("Socket is not initialized");
    }
    this.socket.onmessage = (event) => {
      try {
        const parsedData = JSON.parse(event.data);
        const parseResult = RoomMessageSchema.safeParse(parsedData);

        if (parseResult.success) {
          this.onMessage(parseResult.data);
        } else {
          this.onError(
            new Error(`Invalid message format: ${parseResult.error.message}`),
          );
        }
      } catch (error) {
        this.onError(new Error(`Failed to parse message: ${error}`));
      }
    };

    this.socket.onopen = () => {
      this.onOpen();
    };

    this.socket.onclose = () => {
      this.onClose();
    };

    this.socket.onerror = (event) => {
      this.onError(new Error(`WebSocket error: ${event}`));
    };
  }

  send(message: RoomMessage): void {
    if (!this.socket) {
      throw new Error("Socket is not initialized");
    }
    if (this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    } else {
      this.onError(new Error("WebSocket is not open"));
    }
  }

  open(roomId: string): void {
    const protocol = this.tls ? "wss:" : "ws:";
    const wsUrl = `${protocol}//${this.host}${API_PATHS.WEBSOCKET(roomId)}`;

    this.socket = new WebSocket(wsUrl);
    this.setupEventListeners();
  }

  close(): void {
    if (!this.socket) {
      throw new Error("Socket is not initialized");
    }
    this.socket.close();
  }

  onMessage(message: RoomMessage): void {
    this.messageHandler?.(message);
  }

  onClose(): void {
    this.closeHandler?.();
  }

  onOpen(): void {
    this.openHandler?.();
  }

  onError(error: Error): void {
    this.errorHandler?.(error);
  }

  setMessageHandler(handler: (message: RoomMessage) => void): void {
    this.messageHandler = handler;
  }

  setCloseHandler(handler: () => void): void {
    this.closeHandler = handler;
  }

  setOpenHandler(handler: () => void): void {
    this.openHandler = handler;
  }

  setErrorHandler(handler: (error: Error) => void): void {
    this.errorHandler = handler;
  }
}

class RoomConnectionHandler {
  private transport: LiveTransport<RoomMessage>;
  private userId: string;
  private clientId: string;
  private roomId: string;

  constructor(
    transport: LiveTransport<RoomMessage>,
    userId: string,
    clientId: string,
    roomId: string,
  ) {
    this.transport = transport;
    this.userId = userId;
    this.clientId = clientId;
    this.roomId = roomId;

    this.connect();
  }

  setMessageHandler(handler: (message: RoomMessage) => void): void {
    this.transport.setMessageHandler(handler);
  }

  updateCursor(
    longitude: number,
    latitude: number,
    viewBounds: { north: number; south: number; east: number; west: number },
  ): void {
    const message: RoomMessage = {
      userId: this.userId,
      clientId: this.clientId,
      type: "cursor_update",
      cursor: {
        longitude,
        latitude,
        viewBounds,
      },
    };
    this.transport.send(message);
  }

  leaveCursor(): void {
    const message: RoomMessage = {
      userId: this.userId,
      clientId: this.clientId,
      type: "cursor_leave",
    };
    this.transport.send(message);
  }

  connect(): void {
    this.transport.open(this.roomId);
  }

  disconnect(): void {
    this.transport.close();
  }
}

export {
  type LiveTransport,
  RoomConnectionHandler,
  type RoomMessage,
  RoomMessageSchema,
  WebsocketRoomTransport,
};
